import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgApexchartsModule } from "ng-apexcharts";
import { CoreService } from "../../../services/core.service";

@Component({
  selector: "app-social-ai-follower-growth-chart",
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  templateUrl: "./social-ai-follower-growth-chart.component.html",
  styleUrl: "./social-ai-follower-growth-chart.component.scss",
})
export class SocialAiFollowerGrowthChartComponent implements OnChanges {
  @Input() title: string = "";
  @Input() selectedSiteId: number | null = null;
  @Input() selectedPlatformId: number | null = null;
  @Input() selectedPeriod: string = "days_28";
  @Input() pages: any[] = [];

  isLoaded = false;
  chartOptions: any = {};
  lastChartValue: number | null = null;
  diffChartValue: number | null = null;
  companyID: number | null = null;

  constructor(private coreService: CoreService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["selectedSiteId"] ||
      changes["selectedPlatformId"] ||
      changes["selectedPeriod"] ||
      changes["pages"]
    ) {
      this.loadFollowersChart();
    }
  }

  loadFollowersChart(): void {
    if (!this.selectedSiteId || !this.selectedPlatformId || !this.pages?.length) return;

    this.companyID = this.coreService.user?.companyID || 0;
    this.isLoaded = false;
    this.lastChartValue = null;
    this.diffChartValue = null;

    const platformName = (this.pages[0]?.platform || "").toLowerCase();
    const isInstagram =
      platformName.includes("instagram") ||
      platformName.includes("insta") ||
      platformName.includes("ig");
    const isTikTok = platformName.includes("tiktok");

    // Show TikTok message and return early if TikTok is selected
    if (isTikTok) {
      this.isLoaded = true;
      return;
    }

    const periodToUse = isInstagram ? "day" : this.selectedPeriod;

    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: periodToUse,
      pages: this.pages,
    };

    const endpoint = isInstagram
      ? `querymetrics/parseInstaAnalytics/${this.companyID}?metric=follower_count`
      : `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_follows`;

    this.coreService.postData(endpoint, payload).subscribe((res) => {
      const meta = res.meta || {};

      // Instagram
      if (isInstagram && meta.adjustedValues?.length) {
        let adjustedValues: number[] = meta.adjustedValues;
        let dates: string[] = meta.dates || [];

        if (this.selectedPeriod === "week" && adjustedValues.length > 7) {
          const startIndex = adjustedValues.length - 7;
          adjustedValues = adjustedValues.slice(startIndex);
          dates = dates.slice(startIndex);
        }

        const categories =
          dates.length === adjustedValues.length
            ? dates.map((d) => {
                const date = new Date(d.replace("+0000", "Z"));
                date.setDate(date.getDate() + 1);
                return date.toLocaleDateString("el-GR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                });
              })
            : adjustedValues.map((_, i) => `Day ${i + 1}`);

        this.chartOptions = this.buildChartOptions(adjustedValues, categories);
        this.lastChartValue = Number(meta.todayFollowers || adjustedValues.at(-1) || 0);
        this.diffChartValue = (adjustedValues.at(-1) ?? 0) - (adjustedValues[0] ?? 0);
        this.isLoaded = true;
        return;
      }

      // Facebook
      const data = res.data || [];
      const values = data[0]?.insight?.data?.[0]?.values || [];

      if (!values.length) {
        this.chartOptions = this.buildChartOptions([], []);
        this.isLoaded = true;
        return;
      }

      const chartData = values.map((v: any) => ({ end_time: v.end_time, value: v.value }));

      if (this.selectedPeriod === "week" && chartData.length > 7) {
        chartData.splice(0, chartData.length - 7);
      }

      const categories = chartData.map((d: any) =>
        new Date(d.end_time).toLocaleDateString("el-GR", {
          day: "2-digit", month: "2-digit", year: "numeric",
        })
      );

      this.chartOptions = this.buildChartOptions(chartData.map((d: any) => d.value), categories);
      this.lastChartValue = chartData.at(-1)?.value ?? 0;
      this.diffChartValue = (chartData.at(-1)?.value ?? 0) - (chartData[0]?.value ?? 0);
      this.isLoaded = true;
    });
  }

  private buildChartOptions(data: number[], categories: (string | number)[]): any {
    const isMobile = window.innerWidth <= 768;
    return {
      series: [{ name: "Followers", data }],
      chart: {
        type: "area",
        height: isMobile ? 220 : 360,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      stroke: { curve: "smooth", width: 2, colors: ["#6ab7ff"] },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#f1f3f4",
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      yaxis: {
        tickAmount: 5,
        labels: {
          style: { colors: "#777", fontSize: "12px" },
          formatter: (val: number, index: number) => index === 0 ? "" : val.toLocaleString(),
        },
      },
      xaxis: {
        categories,
        tickAmount: 6,
        labels: { style: { colors: "#777", fontSize: "12px" } },
      },
      tooltip: { theme: "light", y: { formatter: (val: number) => val.toLocaleString("el-GR") } },
      legend: {
        show: !isMobile,
        position: "bottom",
        horizontalAlign: "center",
        labels: { colors: "#888" }
      },
    };
  }
}
